import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Paper,
  Fade,
  Badge,
  Container,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  Science as ScienceIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Speed as SpeedIcon,
  Clear as ClearIcon,
  AutoAwesome as AutoAwesomeIcon,
  Star as StarIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  TipsAndUpdates as TipsIcon,
  Lightbulb as LightbulbIcon,
  CompareArrows as CompareArrowsIcon,
  AltRoute as AltRouteIcon
} from '@mui/icons-material';
import { elementGroupsService, ChemicalElement } from '../../services/elementGroupsService';
import { useElementMethodMatching } from '../../hooks/useElementMethodMatching';
import { alpha } from '@mui/material/styles';
import laboratoryMethodsAPIService from '../../services/laboratoryMethodsAPIService';

interface ElementSelectorProps {
  selectedElements: string[];
  onElementsChange: (elements: string[]) => void;
  onMethodsChange: (methodCodes: string[]) => void;
  onCustomRoutingChange?: (routing: Record<string, string> | null) => void;
  onElementMethodMappingChange?: (mapping: Record<string, string> | null) => void;
  disabled?: boolean;
}

// 🔒 FALLBACK: Original hardcoded methods for safety
// These will be used if database loading fails
const FALLBACK_MULTI_ELEMENT_METHODS = {
  'PRE.MO/ANA/002': {
    elements: ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'],
    laboratory: 'LABORATOIRE_SAA',
    is_accredited: true,
    name: 'AA (9 elements)',
    typical_tat_hours: 24
  },
  'PRE.MO/ANA/038': {
    elements: [
      'Al2O3', 'CaO', 'Fe2O3', 'K2O', 'MgO', 'MnO', 'Na2O', 'P2O5', 'S', 'TiO2',
      'Ag', 'As', 'B', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu',
      'Ga', 'Ge', 'In', 'Li', 'Mo', 'Nb', 'Ni', 'Pb', 'Sb', 'Se',
      'Sn', 'Sr', 'Ta', 'Th', 'Tl', 'V', 'W', 'Y', 'Zn', 'Zr'
    ],
    laboratory: 'LABORATOIRE_ICP',
    is_accredited: true,
    name: 'Roche ICP - 4acide (40 elements)',
    typical_tat_hours: 48
  },
  'PRE.MO/ANA/037': {
    elements: [
      'Al2O3', 'CaO', 'Fe2O3', 'K2O', 'MgO', 'MnO', 'Na2O', 'P2O5', 'S', 'TiO2',
      'Ag', 'As', 'B', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu',
      'Ga', 'Ge', 'In', 'Li', 'Mo', 'Nb', 'Ni', 'Pb', 'Sb', 'Se',
      'Sn', 'Sr', 'Ta', 'Th', 'Tl', 'V', 'W', 'Zn', 'Zr'
    ],
    laboratory: 'LABORATOIRE_ICP',
    is_accredited: true,
    name: 'Stream/Sol ICP - 4acide (40 elements)',
    typical_tat_hours: 48
  },
  'PRE.MO/ANA/007': {
    elements: [
      'Al2O3', 'CaO', 'Fe2O3', 'K2O', 'MgO', 'MnO', 'P2O5', 'S', 'SiO2', 'TiO2',
      'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Li', 'Mo',
      'Ni', 'Pb', 'Sb', 'Se', 'Sn', 'Sr', 'Ta', 'Tl', 'V', 'Y', 'Zn'
    ],
    laboratory: 'LABORATOIRE_ICP',
    is_accredited: false,
    name: 'ICP fusion (32 elements)',
    typical_tat_hours: 48
  },
  'PRE.MO/ANA/039': {
    elements: ['Al2O3', 'CaO', 'Fe2O3', 'K2O', 'MgO', 'MnO', 'P2O5', 'S', 'SiO2', 'TiO2'],
    laboratory: 'LABORATOIRE_ICP',
    is_accredited: false,
    name: 'ICP Majeurs (10 elements)',
    typical_tat_hours: 24
  },
  'PRE.MO/ANA/040': {
    elements: ['Ce', 'Dy', 'Er', 'Eu', 'Gd', 'Ho', 'La', 'Lu', 'Nd', 'Pr', 'Sc', 'Sm', 'Tm', 'U', 'Yb'],
    laboratory: 'LABORATOIRE_ICP',
    is_accredited: false,
    name: 'Terre rare',
    typical_tat_hours: 48
  },
  'PRE.MO/ANA/041': {
    elements: [
      'Al2O3', 'CaO', 'Fe2O3', 'K2O', 'MgO', 'MnO', 'Na2O', 'P2O5', 'S', 'TiO2',
      'Ag', 'As', 'B', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu',
      'Ga', 'Ge', 'In', 'Li', 'Mo', 'Nb', 'Ni', 'Pb', 'Sb', 'Se',
      'Sn', 'Sr', 'Ta', 'Th', 'Tl', 'V', 'W', 'Y', 'Zn', 'Zr',
      'Ce', 'Dy', 'Er', 'Eu', 'Gd', 'Ho', 'La', 'Lu', 'Nd', 'Pr', 'Sc', 'Sm', 'Tm', 'U', 'Yb'
    ],
    laboratory: 'LABORATOIRE_ICP',
    is_accredited: true,
    name: 'Stream/Sol - Terre rare ICP - 4acide (55 elements)',
    typical_tat_hours: 48
  },
  'BGM-MULTI-LAB': {
    elements: ['Ag', 'As', 'Cu', 'Fe', 'Mo', 'Pb', 'Sn', 'Zn', 'Au'],
    laboratory: 'LABORATOIRE_ICP',
    is_accredited: true,
    name: 'BGM Méthode - Multi-laboratoires (8 ICP + 1 AU)',
    typical_tat_hours: 72,
    special_routing: {
      'Au': 'LABORATOIRE_AU',
      'Ag': 'LABORATOIRE_ICP',
      'As': 'LABORATOIRE_ICP',
      'Cu': 'LABORATOIRE_ICP',
      'Fe': 'LABORATOIRE_ICP',
      'Mo': 'LABORATOIRE_ICP',
      'Pb': 'LABORATOIRE_ICP',
      'Sn': 'LABORATOIRE_ICP',
      'Zn': 'LABORATOIRE_ICP'
    }
  },
  // Physico-Chimique Methods
  'PRE.MO/ANA/047': {
    elements: ['DENSITE'],
    laboratory: 'LABORATOIRE_PHYSICO_CHIMIQUE',
    is_accredited: true,
    name: 'Densité',
    typical_tat_hours: 24
  },
  'PRE.MO/ANA/046': {
    elements: ['H2O'],
    laboratory: 'LABORATOIRE_PHYSICO_CHIMIQUE',
    is_accredited: true,
    name: 'Teneur en eau (H₂O)',
    typical_tat_hours: 24
  },
  'PRE.MO/ANA/050': {
    elements: ['PAF'],
    laboratory: 'LABORATOIRE_PHYSICO_CHIMIQUE',
    is_accredited: true,
    name: 'Perte au Feu (PAF)',
    typical_tat_hours: 24
  },
  'PRE.MO/ANA/022': {
    elements: ['MO'],
    laboratory: 'LABORATOIRE_PHYSICO_CHIMIQUE',
    is_accredited: true,
    name: 'Matière Organique (MO)',
    typical_tat_hours: 24
  },
  'PRE.MO/ANA/025': {
    elements: ['PH'],
    laboratory: 'LABORATOIRE_PHYSICO_CHIMIQUE',
    is_accredited: true,
    name: 'pH',
    typical_tat_hours: 24
  },
  'PRE.MO/ANA/045': {
    elements: ['BLANCHEUR_L', 'BLANCHEUR_A', 'BLANCHEUR_B'],
    laboratory: 'LABORATOIRE_PHYSICO_CHIMIQUE',
    is_accredited: true,
    name: 'Blancheur (L*a*b*)',
    typical_tat_hours: 24
  },
  'PRE.MO/ANA/048': {
    elements: ['D80'],  // GRANULO/D80 combined as single element
    laboratory: 'LABORATOIRE_PHYSICO_CHIMIQUE',
    is_accredited: true,
    name: 'Granulométrie',
    typical_tat_hours: 48
  }
};

// Periodic Table Layout - Real periodic table structure
const PERIODIC_TABLE_LAYOUT = [
  // Period 1
  [
    { symbol: 'H', position: 1, group: 1, period: 1 },
    null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null,
    { symbol: 'He', position: 2, group: 18, period: 1 }
  ],
  // Period 2
  [
    { symbol: 'Li', position: 3, group: 1, period: 2 },
    { symbol: 'Be', position: 4, group: 2, period: 2 },
    null, null, null, null, null, null, null, null, null, null,
    { symbol: 'B', position: 5, group: 13, period: 2 },
    { symbol: 'C', position: 6, group: 14, period: 2 },
    { symbol: 'N', position: 7, group: 15, period: 2 },
    { symbol: 'O', position: 8, group: 16, period: 2 },
    { symbol: 'F', position: 9, group: 17, period: 2 },
    { symbol: 'Ne', position: 10, group: 18, period: 2 }
  ],
  // Period 3
  [
    { symbol: 'Na', position: 11, group: 1, period: 3 },
    { symbol: 'Mg', position: 12, group: 2, period: 3 },
    null, null, null, null, null, null, null, null, null, null,
    { symbol: 'Al', position: 13, group: 13, period: 3 },
    { symbol: 'Si', position: 14, group: 14, period: 3 },
    { symbol: 'P', position: 15, group: 15, period: 3 },
    { symbol: 'S', position: 16, group: 16, period: 3 },
    { symbol: 'Cl', position: 17, group: 17, period: 3 },
    { symbol: 'Ar', position: 18, group: 18, period: 3 }
  ],
  // Period 4
  [
    { symbol: 'K', position: 19, group: 1, period: 4 },
    { symbol: 'Ca', position: 20, group: 2, period: 4 },
    { symbol: 'Sc', position: 21, group: 3, period: 4 },
    { symbol: 'Ti', position: 22, group: 4, period: 4 },
    { symbol: 'V', position: 23, group: 5, period: 4 },
    { symbol: 'Cr', position: 24, group: 6, period: 4 },
    { symbol: 'Mn', position: 25, group: 7, period: 4 },
    { symbol: 'Fe', position: 26, group: 8, period: 4 },
    { symbol: 'Co', position: 27, group: 9, period: 4 },
    { symbol: 'Ni', position: 28, group: 10, period: 4 },
    { symbol: 'Cu', position: 29, group: 11, period: 4 },
    { symbol: 'Zn', position: 30, group: 12, period: 4 },
    { symbol: 'Ga', position: 31, group: 13, period: 4 },
    { symbol: 'Ge', position: 32, group: 14, period: 4 },
    { symbol: 'As', position: 33, group: 15, period: 4 },
    { symbol: 'Se', position: 34, group: 16, period: 4 },
    { symbol: 'Br', position: 35, group: 17, period: 4 },
    { symbol: 'Kr', position: 36, group: 18, period: 4 }
  ],
  // Period 5
  [
    { symbol: 'Rb', position: 37, group: 1, period: 5 },
    { symbol: 'Sr', position: 38, group: 2, period: 5 },
    { symbol: 'Y', position: 39, group: 3, period: 5 },
    { symbol: 'Zr', position: 40, group: 4, period: 5 },
    { symbol: 'Nb', position: 41, group: 5, period: 5 },
    { symbol: 'Mo', position: 42, group: 6, period: 5 },
    { symbol: 'Tc', position: 43, group: 7, period: 5 },
    { symbol: 'Ru', position: 44, group: 8, period: 5 },
    { symbol: 'Rh', position: 45, group: 9, period: 5 },
    { symbol: 'Pd', position: 46, group: 10, period: 5 },
    { symbol: 'Ag', position: 47, group: 11, period: 5 },
    { symbol: 'Cd', position: 48, group: 12, period: 5 },
    { symbol: 'In', position: 49, group: 13, period: 5 },
    { symbol: 'Sn', position: 50, group: 14, period: 5 },
    { symbol: 'Sb', position: 51, group: 15, period: 5 },
    { symbol: 'Te', position: 52, group: 16, period: 5 },
    { symbol: 'I', position: 53, group: 17, period: 5 },
    { symbol: 'Xe', position: 54, group: 18, period: 5 }
  ],
  // Period 6
  [
    { symbol: 'Cs', position: 55, group: 1, period: 6 },
    { symbol: 'Ba', position: 56, group: 2, period: 6 },
    { symbol: 'La*', position: 57, group: 3, period: 6, isLanthanide: true },
    { symbol: 'Hf', position: 72, group: 4, period: 6 },
    { symbol: 'Ta', position: 73, group: 5, period: 6 },
    { symbol: 'W', position: 74, group: 6, period: 6 },
    { symbol: 'Re', position: 75, group: 7, period: 6 },
    { symbol: 'Os', position: 76, group: 8, period: 6 },
    { symbol: 'Ir', position: 77, group: 9, period: 6 },
    { symbol: 'Pt', position: 78, group: 10, period: 6 },
    { symbol: 'Au', position: 79, group: 11, period: 6 },
    { symbol: 'Hg', position: 80, group: 12, period: 6 },
    { symbol: 'Tl', position: 81, group: 13, period: 6 },
    { symbol: 'Pb', position: 82, group: 14, period: 6 },
    { symbol: 'Bi', position: 83, group: 15, period: 6 },
    { symbol: 'Po', position: 84, group: 16, period: 6 },
    { symbol: 'At', position: 85, group: 17, period: 6 },
    { symbol: 'Rn', position: 86, group: 18, period: 6 }
  ],
  // Period 7
  [
    { symbol: 'Fr', position: 87, group: 1, period: 7 },
    { symbol: 'Ra', position: 88, group: 2, period: 7 },
    { symbol: 'Ac*', position: 89, group: 3, period: 7, isActinide: true },
    { symbol: 'Rf', position: 104, group: 4, period: 7 },
    { symbol: 'Db', position: 105, group: 5, period: 7 },
    { symbol: 'Sg', position: 106, group: 6, period: 7 },
    { symbol: 'Bh', position: 107, group: 7, period: 7 },
    { symbol: 'Hs', position: 108, group: 8, period: 7 },
    { symbol: 'Mt', position: 109, group: 9, period: 7 },
    { symbol: 'Ds', position: 110, group: 10, period: 7 },
    { symbol: 'Rg', position: 111, group: 11, period: 7 },
    { symbol: 'Cn', position: 112, group: 12, period: 7 },
    { symbol: 'Nh', position: 113, group: 13, period: 7 },
    { symbol: 'Fl', position: 114, group: 14, period: 7 },
    { symbol: 'Mc', position: 115, group: 15, period: 7 },
    { symbol: 'Lv', position: 116, group: 16, period: 7 },
    { symbol: 'Ts', position: 117, group: 17, period: 7 },
    { symbol: 'Og', position: 118, group: 18, period: 7 }
  ]
];

// Lanthanides and Actinides (shown separately)
const LANTHANIDES = [
  { symbol: 'La', position: 57, group: 3, period: 6 },
  { symbol: 'Ce', position: 58, group: 3, period: 6 },
  { symbol: 'Pr', position: 59, group: 3, period: 6 },
  { symbol: 'Nd', position: 60, group: 3, period: 6 },
  { symbol: 'Pm', position: 61, group: 3, period: 6 },
  { symbol: 'Sm', position: 62, group: 3, period: 6 },
  { symbol: 'Eu', position: 63, group: 3, period: 6 },
  { symbol: 'Gd', position: 64, group: 3, period: 6 },
  { symbol: 'Tb', position: 65, group: 3, period: 6 },
  { symbol: 'Dy', position: 66, group: 3, period: 6 },
  { symbol: 'Ho', position: 67, group: 3, period: 6 },
  { symbol: 'Er', position: 68, group: 3, period: 6 },
  { symbol: 'Tm', position: 69, group: 3, period: 6 },
  { symbol: 'Yb', position: 70, group: 3, period: 6 },
  { symbol: 'Lu', position: 71, group: 3, period: 6 }
];

const ACTINIDES = [
  { symbol: 'Ac', position: 89, group: 3, period: 7 },
  { symbol: 'Th', position: 90, group: 3, period: 7 },
  { symbol: 'Pa', position: 91, group: 3, period: 7 },
  { symbol: 'U', position: 92, group: 3, period: 7 },
  { symbol: 'Np', position: 93, group: 3, period: 7 },
  { symbol: 'Pu', position: 94, group: 3, period: 7 },
  { symbol: 'Am', position: 95, group: 3, period: 7 },
  { symbol: 'Cm', position: 96, group: 3, period: 7 },
  { symbol: 'Bk', position: 97, group: 3, period: 7 },
  { symbol: 'Cf', position: 98, group: 3, period: 7 },
  { symbol: 'Es', position: 99, group: 3, period: 7 },
  { symbol: 'Fm', position: 100, group: 3, period: 7 },
  { symbol: 'Md', position: 101, group: 3, period: 7 },
  { symbol: 'No', position: 102, group: 3, period: 7 },
  { symbol: 'Lr', position: 103, group: 3, period: 7 }
];

// 🆕 COMPOUND ELEMENTS - Oxides, Carbonates, Fluorides (not in periodic table)
const COMPOUND_ELEMENTS = [
  { symbol: 'Al2O3', name: 'Oxyde d\'aluminium (Alumine)', category: 'OXIDE', color: '#2196F3' },
  { symbol: 'CaO', name: 'Oxyde de calcium (Chite vive)', category: 'OXIDE', color: '#4CAF50' },
  { symbol: 'Fe2O3', name: 'Oxyde de fer (Hématite)', category: 'OXIDE', color: '#FF5722' },
  { symbol: 'K2O', name: 'Oxyde de potassium', category: 'OXIDE', color: '#8BC34A' },
  { symbol: 'MgO', name: 'Oxyde de magnésium (Magnésie)', category: 'OXIDE', color: '#00BCD4' },
  { symbol: 'MnO', name: 'Oxyde de manganèse', category: 'OXIDE', color: '#9C27B0' },
  { symbol: 'Na2O', name: 'Oxyde de sodium', category: 'OXIDE', color: '#CDDC39' },
  { symbol: 'P2O5', name: 'Pentoxyde de phosphore', category: 'OXIDE', color: '#FF9800' },
  { symbol: 'SiO2', name: 'Dioxyde de silicium (Silice)', category: 'OXIDE', color: '#607D8B' },
  { symbol: 'TiO2', name: 'Dioxyde de titane', category: 'OXIDE', color: '#795548' },
  { symbol: 'CaCO3', name: 'Carbonate de calcium (Calcite)', category: 'CARBONATE', color: '#E91E63' },
  { symbol: 'CaF2', name: 'Fluorure de calcium (Fluorine)', category: 'FLUORIDE', color: '#673AB7' },
];

const ElementSelector: React.FC<ElementSelectorProps> = ({
  selectedElements,
  onElementsChange,
  onMethodsChange,
  onCustomRoutingChange,
  onElementMethodMappingChange,
  disabled = false
}) => {
  console.log('🎛️ ElementSelector rendered with props:', {
    selectedElements,
    disabled,
    hasOnMethodsChange: !!onMethodsChange
  });

  const [availableMethods, setAvailableMethods] = useState<any[]>([]);
  const [selectedMethods, setSelectedMethods] = useState<string[]>([]);
  
  // 🔬 CUSTOM LAB ROUTING: State for multi-lab element routing
  const [customRoutingEnabled, setCustomRoutingEnabled] = useState<boolean>(false);
  const [elementLabAssignments, setElementLabAssignments] = useState<Record<string, string>>({});
  
  // 🆕 NEW: Element-to-Method mapping for correct detection limits
  const [elementMethodMapping, setElementMethodMapping] = useState<Record<string, string>>({});
  
  // 🆕 NEW: Method selection modal state
  const [methodModalOpen, setMethodModalOpen] = useState(false);
  const [methodModalElement, setMethodModalElement] = useState<string>('');
  const [methodModalLab, setMethodModalLab] = useState<string>('');
  const [methodModalMethods, setMethodModalMethods] = useState<any[]>([]);
  const [methodModalLoading, setMethodModalLoading] = useState(false);
  
  // 🆕 NEW: Database-driven method loading
  const [multiElementMethods, setMultiElementMethods] = useState<Record<string, any>>(FALLBACK_MULTI_ELEMENT_METHODS);
  const [methodShortcuts, setMethodShortcuts] = useState<any[]>([]);
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [methodsError, setMethodsError] = useState<string | null>(null);
  
  const {
    loading,
    error,
    getMethodsForElements
  } = useElementMethodMatching();

  // Get elements with methods for highlighting
  const allElements = elementGroupsService.getAllElements();
  const elementsWithMethods = elementGroupsService.getElementsWithMethods().map(e => e.symbol);

  // 🆕 NEW: Load methods from database on component mount
  useEffect(() => {
    loadMethodsFromDatabase();
  }, []);

  const loadMethodsFromDatabase = async () => {
    try {
      setMethodsLoading(true);
      setMethodsError(null);

      console.log('🔄 Loading methods from database...');
      
      // Load both multi-element methods and shortcuts from database
      const [multiElementData, shortcutsData] = await Promise.all([
        laboratoryMethodsAPIService.getMultiElementMethods(),
        laboratoryMethodsAPIService.getMethodShortcuts()
      ]);

      console.log('✅ Database methods loaded successfully:', {
        multiElementCount: Object.keys(multiElementData).length,
        shortcutsCount: shortcutsData.length
      });

      // Update state with database data
      setMultiElementMethods(multiElementData);
      setMethodShortcuts(shortcutsData);
      
    } catch (error) {
      console.error('❌ Failed to load methods from database:', error);
      setMethodsError('Connexion à la base de données échouée. Utilisation des méthodes par défaut.');
      
      // Generate fallback shortcuts from hardcoded data
      const RARE_EARTHS = ['La','Ce','Pr','Nd','Sm','Eu','Gd','Tb','Dy','Ho','Er','Tm','Yb','Lu'];
      const fallbackShortcuts = [
        {
          id: 'icp_55',
          name: '55 Éléments ICP (Terres Rares)',
          description: 'PRE.MO/ANA/038 (Roche ICP 4-acide) + Terres Rares',
          methodCode: 'PRE.MO/ANA/038',
          elements: [...FALLBACK_MULTI_ELEMENT_METHODS['PRE.MO/ANA/038'].elements, ...RARE_EARTHS],
          elementCount: 55,
          icon: '✨',
          color: '#1565C0',
          isAccredited: true
        },
        {
          id: 'icp_40_roche',
          name: '40 Éléments ICP (Roche)',
          description: 'PRE.MO/ANA/038 (Roche ICP 4-acide)',
          methodCode: 'PRE.MO/ANA/038',
          elements: FALLBACK_MULTI_ELEMENT_METHODS['PRE.MO/ANA/038'].elements,
          elementCount: 40,
          icon: '✅',
          color: '#2196F3',
          isAccredited: true
        },
        {
          id: 'icp_40_stream',
          name: '40 Éléments ICP (Stream/Sol)',
          description: 'PRE.MO/ANA/037 (Stream/Sol ICP 4-acide)',
          methodCode: 'PRE.MO/ANA/037',
          elements: FALLBACK_MULTI_ELEMENT_METHODS['PRE.MO/ANA/037'].elements,
          elementCount: 40,
          icon: '✅',
          color: '#00BCD4',
          isAccredited: true
        },
        {
          id: 'icp_32_fusion',
          name: '32 Éléments ICP (Fusion)',
          description: 'PRE.MO/ANA/007 (ICP fusion)',
          methodCode: 'PRE.MO/ANA/007',
          elements: FALLBACK_MULTI_ELEMENT_METHODS['PRE.MO/ANA/007'].elements,
          elementCount: 32,
          icon: '🔥',
          color: '#E91E63',
          isAccredited: false
        },
        {
          id: 'icp_10_majeurs',
          name: '10 Éléments Majeurs',
          description: 'PRE.MO/ANA/039 (ICP Majeurs)',
          methodCode: 'PRE.MO/ANA/039',
          elements: FALLBACK_MULTI_ELEMENT_METHODS['PRE.MO/ANA/039'].elements,
          elementCount: 10,
          icon: '⚗️',
          color: '#FF9800',
          isAccredited: false
        },
        {
          id: 'terre_rare',
          name: 'Terre Rare (15 éléments)',
          description: 'PRE.MO/ANA/040 (Terres Rares par ICP)',
          methodCode: 'PRE.MO/ANA/040',
          elements: FALLBACK_MULTI_ELEMENT_METHODS['PRE.MO/ANA/040'].elements,
          elementCount: 15,
          icon: '🌟',
          color: '#673AB7',
          isAccredited: false
        },
        {
          id: 'stream_terre_rare',
          name: 'Stream/Sol + Terre Rare (55 éléments)',
          description: 'PRE.MO/ANA/041 (Stream/Sol + Terres Rares)',
          methodCode: 'PRE.MO/ANA/041',
          elements: FALLBACK_MULTI_ELEMENT_METHODS['PRE.MO/ANA/041'].elements,
          elementCount: 55,
          icon: '✨',
          color: '#3F51B5',
          isAccredited: true
        },
        {
          id: 'bgm_method',
          name: 'BGM Méthode',
          description: 'BGM-MULTI-LAB (8 ICP + 1 AU)',
          methodCode: 'BGM-MULTI-LAB',
          elements: FALLBACK_MULTI_ELEMENT_METHODS['BGM-MULTI-LAB'].elements,
          elementCount: 9,
          icon: '✅',
          color: '#9C27B0',
          isAccredited: true,
          isMultiLab: true,
          specialRouting: FALLBACK_MULTI_ELEMENT_METHODS['BGM-MULTI-LAB'].special_routing
        }
      ];
      
      setMethodShortcuts(fallbackShortcuts);
      
    } finally {
      setMethodsLoading(false);
    }
  };

  // Handle method shortcut button clicks
  // 🔧 FIX: Also set element_method_mapping for all elements in the shortcut
  const handleMethodShortcut = (shortcut: any) => {
    onElementsChange(shortcut.elements);
    setSelectedMethods([shortcut.methodCode]);
    onMethodsChange([shortcut.methodCode]);
    
    // 🆕 FIX: Set element_method_mapping for ALL elements in the shortcut
    // This ensures detection limits are correctly applied on certificates
    const mapping: Record<string, string> = {};
    shortcut.elements.forEach((el: string) => {
      mapping[el] = shortcut.methodCode;
    });
    setElementMethodMapping(mapping);
    console.log(`✅ Method shortcut applied: ${shortcut.methodCode} for ${shortcut.elements.length} elements`);
  };

  // Handle element selection
  const handleElementToggle = (elementSymbol: string) => {
    if (disabled) return;
    
    const newElements = selectedElements.includes(elementSymbol)
      ? selectedElements.filter(el => el !== elementSymbol)
      : [...selectedElements, elementSymbol];
    
    console.log('🎯 Element toggled:', elementSymbol, 'New selection:', newElements);
    onElementsChange(newElements);
  };

  // Handle method selection
  const handleToggleMethod = (methodCode: string) => {
    const newSelectedMethods = selectedMethods.includes(methodCode)
      ? selectedMethods.filter(m => m !== methodCode)
      : [...selectedMethods, methodCode];
    
    setSelectedMethods(newSelectedMethods);
    onMethodsChange(newSelectedMethods);
  };

  const clearSelection = () => {
    onElementsChange([]);
    setSelectedMethods([]);
    onMethodsChange([]);
  };

  // Get element category color for styling
  const getElementColor = (symbol: string) => {
    const element = allElements.find(e => e.symbol === symbol);
    if (!element) return '#grey';
    
    const categoryColors = {
      'PRECIOUS_METALS': '#FFD700',
      'BASE_METALS': '#4CAF50',
      'MAJOR_ELEMENTS': '#2196F3',
      'TRACE_ELEMENTS': '#9C27B0',
      'RARE_EARTH': '#FF9800',
      'TRANSITION_METALS': '#FF5722',
      'ACTINIDES': '#E91E63',
      'ALKALI_METALS': '#8BC34A',
      'ALKALINE_EARTH': '#CDDC39',
      'METALLOIDS': '#607D8B',
      'HALOGENS': '#00BCD4',
      'NOBLE_GASES': '#9E9E9E',
      'OTHER_NONMETALS': '#795548'
    };
    
    return categoryColors[element.category as keyof typeof categoryColors] || '#9E9E9E';
  };

  // Render a single element cell
  const renderElementCell = (elementData: any) => {
    if (!elementData) {
      return <Box key="empty" sx={{ width: 45, height: 45, m: 0.25 }} />;
    }

    const { symbol } = elementData;
    const isSelected = selectedElements.includes(symbol);
    const hasMethod = elementsWithMethods.includes(symbol);
    const elementColor = getElementColor(symbol);
    const element = allElements.find(e => e.symbol === symbol);
    const atomicNumber = element?.atomic_number || elementData.position;
    
    return (
      <Tooltip 
        key={symbol} 
        title={
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {element ? element.name : symbol}
            </Typography>
            <Typography variant="body2">
              {symbol} - #{atomicNumber}
            </Typography>
            <Typography variant="caption" sx={{ color: hasMethod ? '#4caf50' : '#ff9800' }}>
              {hasMethod ? '✓ Méthodes disponibles' : '⚠ Pas de méthodes'}
            </Typography>
          </Box>
        }
        arrow
        placement="top"
      >
        <Box
          onClick={() => handleElementToggle(symbol)}
          sx={{
            width: 45,
            height: 45,
            m: 0.25,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: 2,
            borderRadius: 1,
            position: 'relative',
            cursor: hasMethod ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease',
            
            // Border colors
            borderColor: isSelected 
              ? elementColor 
              : hasMethod 
                ? alpha(elementColor, 0.6)
                : alpha('#999', 0.3),
            
            // Background colors - much more visible now!
            backgroundColor: isSelected 
              ? alpha(elementColor, 0.4)  // Strong color when selected
              : hasMethod 
                ? alpha(elementColor, 0.15)  // Visible color when has methods
                : alpha(elementColor, 0.08),  // Still some color even without methods
            
            // Text colors
            color: isSelected 
              ? '#000'
              : hasMethod 
                ? '#000'
                : '#666',
            
            // Font styling
            fontSize: '0.75rem',
            fontWeight: isSelected ? 'bold' : hasMethod ? '600' : 'normal',
            
            // Hover effects
            '&:hover': hasMethod ? {
              backgroundColor: alpha(elementColor, 0.3),
              borderColor: elementColor,
              transform: 'scale(1.1)',
              zIndex: 10,
              boxShadow: `0 4px 12px ${alpha(elementColor, 0.4)}`,
              '& .atomic-number': {
                opacity: 1
              }
            } : {
              backgroundColor: alpha('#999', 0.1),
            },
            
            // Visual indicator for no methods
            '&::after': !hasMethod ? {
              content: '"⚠"',
              position: 'absolute',
              top: 1,
              right: 1,
              fontSize: '8px',
              color: '#ff9800',
              fontWeight: 'bold'
            } : {},
            
            // Visual indicator for selected
            '&::before': isSelected ? {
              content: '"✓"',
              position: 'absolute',
              top: 1,
              left: 1,
              fontSize: '8px',
              color: '#4caf50',
              fontWeight: 'bold',
              backgroundColor: 'white',
              borderRadius: '50%',
              width: 12,
              height: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            } : {}
          }}
        >
          {/* Element Symbol */}
          <Typography 
            variant="body2" 
        sx={{ 
              fontWeight: 'inherit',
              fontSize: 'inherit',
              lineHeight: 1,
              mb: 0.25
            }}
          >
            {symbol}
              </Typography>
          
          {/* Atomic Number */}
          <Typography 
            className="atomic-number"
            variant="caption" 
            sx={{ 
              fontSize: '0.6rem',
              lineHeight: 1,
              opacity: isSelected || !hasMethod ? 1 : 0.7,
              color: 'inherit',
              transition: 'opacity 0.2s ease'
            }}
          >
            {atomicNumber}
            </Typography>
              </Box>
      </Tooltip>
    );
  };

  // Method loading effect (KEEP AS REQUESTED - this is what suggests methods after selection)
  useEffect(() => {
    if (selectedElements.length > 0 && !loading) {
      try {
        const elementMatches = getMethodsForElements(selectedElements);
        const allMethods = elementMatches.flatMap(match => match.availableMethods);
        
        // 🔧 FIX: Deduplicate methods by method_code to prevent React key warnings
        const uniqueMethods = allMethods.filter((method, index, array) => 
          index === array.findIndex(m => m.method_code === method.method_code)
        );
        
        console.log('🔬 Available methods found:', uniqueMethods.length, uniqueMethods.map(m => m.method_code));
        setAvailableMethods(uniqueMethods);
      } catch (err) {
        console.error('Error getting methods:', err);
        setAvailableMethods([]);
      }
    } else if (selectedElements.length === 0) {
      setAvailableMethods([]);
      setSelectedMethods([]);
      onMethodsChange([]);
    }
  }, [selectedElements.join(','), loading]);

  // 🔬 CUSTOM ROUTING: Effect to notify parent when custom routing changes
  useEffect(() => {
    if (onCustomRoutingChange) {
      if (customRoutingEnabled && Object.keys(elementLabAssignments).length > 0) {
        onCustomRoutingChange(elementLabAssignments);
        console.log('🔬 Custom routing updated:', elementLabAssignments);
      } else {
        onCustomRoutingChange(null);
      }
    }
  }, [customRoutingEnabled, elementLabAssignments, onCustomRoutingChange]);

  // 🆕 NEW: Notify parent when element-method mapping changes
  useEffect(() => {
    if (onElementMethodMappingChange) {
      if (Object.keys(elementMethodMapping).length > 0) {
        onElementMethodMappingChange(elementMethodMapping);
        console.log('🔬 Element-method mapping updated:', elementMethodMapping);
      } else {
        onElementMethodMappingChange(null);
      }
    }
  }, [elementMethodMapping, onElementMethodMappingChange]);

  // 🔬 CUSTOM ROUTING: Reset assignments when selected elements change
  // 🔧 FIX: Properly handle Physico-Chimique elements - don't default them to ICP
  // 🆕 FIX: Also ensure default methods are added for auto-assigned labs
  useEffect(() => {
    if (customRoutingEnabled && selectedElements.length > 0) {
      // Initialize default assignments for new elements
      const newAssignments: Record<string, string> = {};
      const physicoElements = ['DENSITE', 'H2O', 'PAF', 'MO', 'PH', 'BLANCHEUR_L', 'BLANCHEUR_A', 'BLANCHEUR_B', 'D80'];
      
      // 🆕 Track which labs have elements assigned
      let hasIcpElements = false;
      let hasAuElements = false;
      let hasSaaElements = false;

      selectedElements.forEach(el => {
        // Keep existing assignment if element was already assigned
        if (elementLabAssignments[el]) {
          newAssignments[el] = elementLabAssignments[el];
          // Track which labs are used
          if (elementLabAssignments[el] === 'LABORATOIRE_ICP') hasIcpElements = true;
          if (elementLabAssignments[el] === 'LABORATOIRE_AU') hasAuElements = true;
          if (elementLabAssignments[el] === 'LABORATOIRE_SAA') hasSaaElements = true;
        } else {
          // 🔧 FIX: Physico elements should default to PHYSICO lab, not ICP
          if (physicoElements.includes(el)) {
            newAssignments[el] = 'LABORATOIRE_PHYSICO_CHIMIQUE';
          } else {
            newAssignments[el] = 'LABORATOIRE_ICP';
            hasIcpElements = true; // New element defaulting to ICP
          }
        }
      });
      setElementLabAssignments(newAssignments);
      
      // 🆕 FIX: Ensure appropriate methods are in selectedMethods for each lab with elements
      // This ensures certificate Methods table is complete
      const methodsToAdd: string[] = [];
      const icpMethods = ['PRE.MO/ANA/038', 'PRE.MO/ANA/037', 'PRE.MO/ANA/007', 'PRE.MO/ANA/039', 'PRE.MO/ANA/040', 'PRE.MO/ANA/041'];
      const auMethods = ['PRE.MO/ANA/001'];
      const saaMethods = ['PRE.MO/ANA/002'];
      
      // Check if ICP elements exist but no ICP method is selected
      if (hasIcpElements && !selectedMethods.some(m => icpMethods.includes(m) || m.includes('ICP'))) {
        methodsToAdd.push('PRE.MO/ANA/038'); // Default ICP method
        console.log('🔧 Auto-adding default ICP method PRE.MO/ANA/038');
      }
      
      // Check if AU elements exist but no AU method is selected
      if (hasAuElements && !selectedMethods.some(m => auMethods.includes(m) || m.includes('Au'))) {
        methodsToAdd.push('PRE.MO/ANA/001'); // Default AU method
        console.log('🔧 Auto-adding default AU method PRE.MO/ANA/001');
      }
      
      // Check if SAA elements exist but no SAA method is selected
      if (hasSaaElements && !selectedMethods.some(m => saaMethods.includes(m) || m.includes('AA'))) {
        methodsToAdd.push('PRE.MO/ANA/002'); // Default SAA method
        console.log('🔧 Auto-adding default SAA method PRE.MO/ANA/002');
      }
      
      // Add missing methods if any
      if (methodsToAdd.length > 0) {
        const newMethods = [...selectedMethods, ...methodsToAdd];
        setSelectedMethods(newMethods);
        onMethodsChange(newMethods);
      }
    }
  }, [selectedElements.join(','), customRoutingEnabled]);

  // 🔬 CUSTOM ROUTING: Helper to set all elements to a specific lab
  const setAllElementsToLab = (lab: string) => {
    const newAssignments: Record<string, string> = {};
    selectedElements.forEach(el => {
      newAssignments[el] = lab;
    });
    setElementLabAssignments(newAssignments);
  };

  // 🆕 NEW: Handle lab change - open modal to select method
  const handleLabChange = async (element: string, newLab: string) => {
    console.log(`🔬 Lab change for ${element} to ${newLab}`);
    
    // For Physico-Chimique, no method selection needed
    if (newLab === 'LABORATOIRE_PHYSICO_CHIMIQUE') {
      setElementLabAssignments(prev => ({ ...prev, [element]: newLab }));
      return;
    }
    
    // For ICP, SAA, AU - fetch available methods and show modal
    setMethodModalElement(element);
    setMethodModalLab(newLab);
    setMethodModalLoading(true);
    setMethodModalOpen(true);
    
    try {
      const response = await fetch(`/api/v1/laboratory-methods/by-element-and-lab/${element}/${newLab}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const methods = await response.json();
        console.log(`✅ Found ${methods.length} methods for ${element} in ${newLab}:`, methods);
        setMethodModalMethods(methods);
        
        // If only 1 method, auto-select it
        if (methods.length === 1) {
          handleMethodSelect(element, newLab, methods[0].method_code);
          setMethodModalOpen(false);
        }
      } else {
        console.error('Failed to fetch methods:', response.status);
        setMethodModalMethods([]);
      }
    } catch (error) {
      console.error('Error fetching methods:', error);
      setMethodModalMethods([]);
    } finally {
      setMethodModalLoading(false);
    }
  };

  // 🆕 NEW: Handle method selection from modal
  const handleMethodSelect = (element: string, lab: string, methodCode: string) => {
    console.log(`✅ Method selected for ${element}: ${methodCode} (lab: ${lab})`);
    
    // Update lab assignment
    setElementLabAssignments(prev => ({ ...prev, [element]: lab }));
    
    // Update element-method mapping
    setElementMethodMapping(prev => ({ ...prev, [element]: methodCode }));
    
    // Add method to selected methods if not already there
    if (!selectedMethods.includes(methodCode)) {
      const newMethods = [...selectedMethods, methodCode];
      setSelectedMethods(newMethods);
      onMethodsChange(newMethods);
    }
    
    // Close modal
    setMethodModalOpen(false);
  };

  // 🆕 NEW: Handle modal cancel - revert to previous lab
  const handleMethodModalCancel = () => {
    setMethodModalOpen(false);
    setMethodModalElement('');
    setMethodModalLab('');
    setMethodModalMethods([]);
  };

  // 🔬 CUSTOM ROUTING: Get lab display name
  const getLabDisplayName = (lab: string): string => {
    switch(lab) {
      case 'LABORATOIRE_SAA': return 'SAA';
      case 'LABORATOIRE_ICP': return 'ICP';
      case 'LABORATOIRE_AU': return 'AU';
      case 'LABORATOIRE_VOLUMETRIE': return 'VOL';
      case 'LABORATOIRE_PHYSICO_CHIMIQUE': return 'PHYSICO';
      default: return lab;
    }
  };

  // 🧪 PHYSICO-CHIMIQUE: Check if element is a Physico-Chimique element
  const isPhysicoChimiqueElement = useCallback((element: string): boolean => {
    const physicoElements = ['DENSITE', 'H2O', 'PAF', 'MO', 'PH', 'BLANCHEUR_L', 'BLANCHEUR_A', 'BLANCHEUR_B', 'D80'];
    return physicoElements.includes(element);
  }, []);

  // 🧪 PHYSICO-CHIMIQUE: Element-to-method mapping for auto-selecting analysis methods
  const PHYSICO_ELEMENT_METHOD_MAP: Record<string, string> = useMemo(() => ({
    'DENSITE': 'PRE.MO/ANA/047',
    'H2O': 'PRE.MO/ANA/046',
    'PAF': 'PRE.MO/ANA/050',
    'MO': 'PRE.MO/ANA/022',
    'PH': 'PRE.MO/ANA/025',
    'BLANCHEUR_L': 'PRE.MO/ANA/045',
    'BLANCHEUR_A': 'PRE.MO/ANA/045',
    'BLANCHEUR_B': 'PRE.MO/ANA/045',
    'D80': 'PRE.MO/ANA/048',
  }), []);

  // 🧪 PHYSICO-CHIMIQUE: Function to handle adding Physico-Chimique element with proper routing
  const handlePhysicoElementToggle = useCallback((element: string) => {
    const isSelected = selectedElements.includes(element);
    
    if (isSelected) {
      // Remove element
      const newElements = selectedElements.filter(el => el !== element);
      onElementsChange(newElements);
      
      // Also remove from routing if exists
      if (elementLabAssignments[element]) {
        const newAssignments = { ...elementLabAssignments };
        delete newAssignments[element];
        setElementLabAssignments(newAssignments);
      }

      // Remove the corresponding physico method if no other selected element uses it
      const methodCode = PHYSICO_ELEMENT_METHOD_MAP[element];
      if (methodCode) {
        const otherElementsUsingMethod = newElements.filter(
          el => PHYSICO_ELEMENT_METHOD_MAP[el] === methodCode
        );
        if (otherElementsUsingMethod.length === 0) {
          const newMethods = selectedMethods.filter(m => m !== methodCode);
          setSelectedMethods(newMethods);
          onMethodsChange(newMethods);
        }
      }
    } else {
      // Add element
      const newElements = [...selectedElements, element];
      onElementsChange(newElements);
      
      // Auto-add the corresponding physico analysis method
      const methodCode = PHYSICO_ELEMENT_METHOD_MAP[element];
      if (methodCode && !selectedMethods.includes(methodCode)) {
        const newMethods = [...selectedMethods, methodCode];
        setSelectedMethods(newMethods);
        onMethodsChange(newMethods);
      }

      // Check if we need multi-lab routing (has both physico and regular elements)
      const hasRegularElements = newElements.some(el => !isPhysicoChimiqueElement(el));
      
      if (hasRegularElements) {
        // Enable custom routing and assign labs appropriately
        if (!customRoutingEnabled) {
          setCustomRoutingEnabled(true);
        }
        
        // Build proper assignments
        const newAssignments: Record<string, string> = { ...elementLabAssignments };
        newElements.forEach(el => {
          if (isPhysicoChimiqueElement(el)) {
            newAssignments[el] = 'LABORATOIRE_PHYSICO_CHIMIQUE';
          } else if (!newAssignments[el]) {
            newAssignments[el] = 'LABORATOIRE_ICP';
          }
        });
        setElementLabAssignments(newAssignments);
      } else {
        // Only physico elements - just add to routing
        const newAssignments: Record<string, string> = { ...elementLabAssignments };
        newAssignments[element] = 'LABORATOIRE_PHYSICO_CHIMIQUE';
        setElementLabAssignments(newAssignments);
        
        if (!customRoutingEnabled) {
          setCustomRoutingEnabled(true);
        }
      }
    }
  }, [selectedElements, onElementsChange, elementLabAssignments, customRoutingEnabled, isPhysicoChimiqueElement, selectedMethods, onMethodsChange, PHYSICO_ELEMENT_METHOD_MAP]);

  // 🔬 CUSTOM ROUTING: Get lab color
  const getLabColor = (lab: string): string => {
    switch(lab) {
      case 'LABORATOIRE_SAA': return '#FF9800';  // Orange
      case 'LABORATOIRE_ICP': return '#2196F3';  // Blue
      case 'LABORATOIRE_AU': return '#FFD700';   // Gold
      case 'LABORATOIRE_VOLUMETRIE': return '#9C27B0';  // Purple
      case 'LABORATOIRE_PHYSICO_CHIMIQUE': return '#059669';  // Green
      default: return '#757575';
    }
  };

  // Render method card
  const renderMethodCard = (method: any, index: number) => {
    const isSelected = selectedMethods.includes(method.method_code);
    
    return (
      <Grid item xs={12} sm={6} md={4} key={method.method_code}>
        <Card 
          elevation={isSelected ? 8 : 2}
          sx={{ 
            height: '100%',
            cursor: 'pointer',
            borderLeft: `4px solid ${method.is_accredited ? '#4caf50' : '#ff9800'}`,
            backgroundColor: isSelected 
              ? alpha(method.is_accredited ? '#4caf50' : '#ff9800', 0.1)
              : 'background.paper',
            '&:hover': {
              elevation: 6,
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
          onClick={() => handleToggleMethod(method.method_code)}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ mr: 1 }}>
                {method.is_accredited ? '✓' : '⚠'}
                </Typography>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {method.method_name}
                </Typography>
                  <Chip 
                  label={method.is_accredited ? "ACCRÉDITÉ" : "NON ACCRÉDITÉ"}
                    color={method.is_accredited ? "success" : "warning"}
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
              </Box>
            </Box>
            
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontSize: '0.85rem' }}>
              Code: {method.method_code}
              </Typography>
            
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.8rem' }}>
              {method.description}
              </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {method.laboratory}
                </Typography>
              <Typography variant="caption" sx={{ color: 'primary.main' }}>
                {method.typical_tat_hours}h
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  // Render methods section (KEEP AS REQUESTED - this suggests methods after selecting elements)
  const renderMethodsSection = () => {
    if (availableMethods.length === 0) return null;

    const accreditedMethods = availableMethods.filter(m => m.is_accredited);
    const nonAccreditedMethods = availableMethods.filter(m => !m.is_accredited);

    return (
      <Accordion sx={{ mt: 4 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <ScienceIcon sx={{ mr: 2, color: 'primary.main' }} />
            Méthodes disponibles pour vos éléments ({availableMethods.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Accredited Methods */}
          {accreditedMethods.length > 0 && (
            <Paper elevation={2} sx={{ mb: 3, borderLeft: '4px solid #4caf50' }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#4caf50' }}>
                  <VerifiedIcon sx={{ mr: 1 }} />
                  Méthodes Accréditées ({accreditedMethods.length})
                </Typography>
                <Grid container spacing={3}>
                  {accreditedMethods.map((method, index) => renderMethodCard(method, index))}
                </Grid>
              </Box>
            </Paper>
          )}

          {/* Non-Accredited Methods */}
          {nonAccreditedMethods.length > 0 && (
            <Paper elevation={2} sx={{ mb: 3, borderLeft: '4px solid #ff9800' }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#ff9800' }}>
                  <WarningIcon sx={{ mr: 1 }} />
                  Méthodes Non Accréditées ({nonAccreditedMethods.length})
                </Typography>
                <Grid container spacing={3}>
                  {nonAccreditedMethods.map((method, index) => renderMethodCard(method, index))}
                </Grid>
              </Box>
            </Paper>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  // Show loading state for method loading or element matching
  if (loading || methodsLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 6 }}>
          <CircularProgress size={80} thickness={4} />
          <Typography sx={{ mt: 3, fontSize: '1.2rem', fontWeight: 'bold' }}>
            {methodsLoading ? '🔄 Chargement des méthodes depuis la base de données...' : '✨ Chargement des méthodes d\'analyse...'}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6">Erreur de chargement</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Method Loading Error */}
      {methodsError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            ⚠️ Problème de connexion base de données
          </Typography>
          <Typography variant="body2">
            {methodsError}
          </Typography>
        </Alert>
      )}

      {/* Selected Elements Summary */}
      {selectedElements.length > 0 && (
        <Fade in={true}>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                  <AutoAwesomeIcon sx={{ mr: 1 }} />
                  Éléments sélectionnés ({selectedElements.length})
                </Typography>
                <Typography variant="body2">
                  {selectedElements.join(' • ')}
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearSelection}
                disabled={disabled}
              >
                Effacer
              </Button>
            </Box>
          </Alert>
        </Fade>
      )}

      {/* Selected Methods Summary */}
      {selectedMethods.length > 0 && (
        <Fade in={true}>
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                  <ScienceIcon sx={{ mr: 1 }} />
                  Méthodes sélectionnées ({selectedMethods.length})
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'Consolas, Monaco, monospace' }}>
                  {selectedMethods.join(' • ')}
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<ClearIcon />}
                onClick={() => {
                  setSelectedMethods([]);
                  onMethodsChange([]);
                }}
                disabled={disabled}
              >
                Effacer méthodes
              </Button>
            </Box>
          </Alert>
        </Fade>
      )}

      {/* Method Shortcut Buttons (KEEP AS REQUESTED - the 4 methods) */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          ⚡ Méthodes Rapides
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sélectionnez rapidement une méthode standard avec ses éléments correspondants
        </Typography>
        
        <Grid container spacing={2}>
          {methodShortcuts.map((shortcut) => (
            <Grid item xs={12} sm={6} md={3} key={shortcut.id}>
              <Card 
                elevation={3}
                sx={{ 
                  height: '100%',
                  cursor: 'pointer',
                  borderLeft: `4px solid ${shortcut.color}`,
                  '&:hover': {
                    elevation: 6,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  },
                  transition: 'all 0.3s ease-in-out',
                  background: shortcut.isAccredited 
                    ? 'linear-gradient(135deg, #f1f8e9 0%, #ffffff 100%)'
                    : 'linear-gradient(135deg, #fff3e0 0%, #ffffff 100%)'
                }}
                onClick={() => handleMethodShortcut(shortcut)}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h4" sx={{ mr: 1 }}>
                      {shortcut.icon}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: shortcut.color }}>
                        {shortcut.name}
                      </Typography>
                      <Chip 
                        label={shortcut.isAccredited ? "✓ ACCRÉDITÉ" : "⚠ NON ACCRÉDITÉ"}
                        color={shortcut.isAccredited ? "success" : "warning"}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    mb: 1, 
                    fontSize: '0.85rem',
                    fontFamily: 'monospace',
                    backgroundColor: '#f5f5f5',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #e0e0e0'
                  }}>
                    {shortcut.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: shortcut.color }}>
                      {shortcut.elementCount || shortcut.elements.length} éléments
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ 
                        backgroundColor: shortcut.color,
                        '&:hover': { backgroundColor: shortcut.color, opacity: 0.8 },
                        fontSize: '0.75rem',
                        px: 2
                      }}
                    >
                      Sélectionner
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* 🧪 PHYSICO-CHIMIQUE ANALYSES SECTION */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #e8f5e9 0%, #ffffff 100%)' }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          🧪 Analyses Physico-Chimiques
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sélectionnez les mesures physico-chimiques à effectuer (densité, teneur en eau, granulométrie, etc.)
        </Typography>
        
        <Grid container spacing={2}>
          {/* Densité */}
          <Grid item xs={6} sm={4} md={2}>
            <Card 
              elevation={selectedElements.includes('DENSITE') ? 8 : 2}
              sx={{ 
                cursor: 'pointer',
                borderLeft: '4px solid #059669',
                backgroundColor: selectedElements.includes('DENSITE') 
                  ? alpha('#059669', 0.15)
                  : 'background.paper',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                transition: 'all 0.3s ease'
              }}
              onClick={() => handlePhysicoElementToggle('DENSITE')}
            >
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">⚖️</Typography>
                <Typography variant="subtitle2" fontWeight="bold">Densité</Typography>
                <Typography variant="caption" color="text.secondary">g/cm³</Typography>
                {selectedElements.includes('DENSITE') && (
                  <Chip label="✓" size="small" color="success" sx={{ mt: 1 }} />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* H2O */}
          <Grid item xs={6} sm={4} md={2}>
            <Card 
              elevation={selectedElements.includes('H2O') ? 8 : 2}
              sx={{ 
                cursor: 'pointer',
                borderLeft: '4px solid #0284c7',
                backgroundColor: selectedElements.includes('H2O') 
                  ? alpha('#0284c7', 0.15)
                  : 'background.paper',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                transition: 'all 0.3s ease'
              }}
              onClick={() => handlePhysicoElementToggle('H2O')}
            >
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">💧</Typography>
                <Typography variant="subtitle2" fontWeight="bold">H₂O</Typography>
                <Typography variant="caption" color="text.secondary">Teneur en eau %</Typography>
                {selectedElements.includes('H2O') && (
                  <Chip label="✓" size="small" color="success" sx={{ mt: 1 }} />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* PAF */}
          <Grid item xs={6} sm={4} md={2}>
            <Card 
              elevation={selectedElements.includes('PAF') ? 8 : 2}
              sx={{ 
                cursor: 'pointer',
                borderLeft: '4px solid #dc2626',
                backgroundColor: selectedElements.includes('PAF') 
                  ? alpha('#dc2626', 0.15)
                  : 'background.paper',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                transition: 'all 0.3s ease'
              }}
              onClick={() => handlePhysicoElementToggle('PAF')}
            >
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">🔥</Typography>
                <Typography variant="subtitle2" fontWeight="bold">PAF</Typography>
                <Typography variant="caption" color="text.secondary">Perte au feu %</Typography>
                {selectedElements.includes('PAF') && (
                  <Chip label="✓" size="small" color="success" sx={{ mt: 1 }} />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* MO */}
          <Grid item xs={6} sm={4} md={2}>
            <Card 
              elevation={selectedElements.includes('MO') ? 8 : 2}
              sx={{ 
                cursor: 'pointer',
                borderLeft: '4px solid #65a30d',
                backgroundColor: selectedElements.includes('MO') 
                  ? alpha('#65a30d', 0.15)
                  : 'background.paper',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                transition: 'all 0.3s ease'
              }}
              onClick={() => handlePhysicoElementToggle('MO')}
            >
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">🌿</Typography>
                <Typography variant="subtitle2" fontWeight="bold">MO</Typography>
                <Typography variant="caption" color="text.secondary">Matière Organique %</Typography>
                {selectedElements.includes('MO') && (
                  <Chip label="✓" size="small" color="success" sx={{ mt: 1 }} />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* pH */}
          <Grid item xs={6} sm={4} md={2}>
            <Card 
              elevation={selectedElements.includes('PH') ? 8 : 2}
              sx={{ 
                cursor: 'pointer',
                borderLeft: '4px solid #7c3aed',
                backgroundColor: selectedElements.includes('PH') 
                  ? alpha('#7c3aed', 0.15)
                  : 'background.paper',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                transition: 'all 0.3s ease'
              }}
              onClick={() => handlePhysicoElementToggle('PH')}
            >
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">🧫</Typography>
                <Typography variant="subtitle2" fontWeight="bold">pH</Typography>
                <Typography variant="caption" color="text.secondary">Acidité/Basicité</Typography>
                {selectedElements.includes('PH') && (
                  <Chip label="✓" size="small" color="success" sx={{ mt: 1 }} />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Blancheur */}
          <Grid item xs={6} sm={4} md={2}>
            <Card 
              elevation={(selectedElements.includes('BLANCHEUR_L') || selectedElements.includes('BLANCHEUR_A') || selectedElements.includes('BLANCHEUR_B')) ? 8 : 2}
              sx={{ 
                cursor: 'pointer',
                borderLeft: '4px solid #f59e0b',
                backgroundColor: (selectedElements.includes('BLANCHEUR_L') || selectedElements.includes('BLANCHEUR_A') || selectedElements.includes('BLANCHEUR_B'))
                  ? alpha('#f59e0b', 0.15)
                  : 'background.paper',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                transition: 'all 0.3s ease'
              }}
              onClick={() => {
                // Toggle all blancheur elements together with proper routing
                const hasBlancheur = selectedElements.includes('BLANCHEUR_L');
                if (hasBlancheur) {
                  // Remove all blancheur elements
                  const newElements = selectedElements.filter(e => !e.startsWith('BLANCHEUR_'));
                  onElementsChange(newElements);
                  // Remove from routing
                  const newAssignments = { ...elementLabAssignments };
                  delete newAssignments['BLANCHEUR_L'];
                  delete newAssignments['BLANCHEUR_A'];
                  delete newAssignments['BLANCHEUR_B'];
                  setElementLabAssignments(newAssignments);
                  // Remove the blancheur method
                  const blancheurMethod = 'PRE.MO/ANA/045';
                  const newMethods = selectedMethods.filter(m => m !== blancheurMethod);
                  setSelectedMethods(newMethods);
                  onMethodsChange(newMethods);
                } else {
                  // Add all blancheur elements with routing
                  const newElements = [...selectedElements, 'BLANCHEUR_L', 'BLANCHEUR_A', 'BLANCHEUR_B'];
                  onElementsChange(newElements);
                  // Add to routing
                  const newAssignments = { ...elementLabAssignments };
                  newAssignments['BLANCHEUR_L'] = 'LABORATOIRE_PHYSICO_CHIMIQUE';
                  newAssignments['BLANCHEUR_A'] = 'LABORATOIRE_PHYSICO_CHIMIQUE';
                  newAssignments['BLANCHEUR_B'] = 'LABORATOIRE_PHYSICO_CHIMIQUE';
                  setElementLabAssignments(newAssignments);
                  if (!customRoutingEnabled) setCustomRoutingEnabled(true);
                  // Add the blancheur method
                  const blancheurMethod = 'PRE.MO/ANA/045';
                  if (!selectedMethods.includes(blancheurMethod)) {
                    const newMethods = [...selectedMethods, blancheurMethod];
                    setSelectedMethods(newMethods);
                    onMethodsChange(newMethods);
                  }
                }
              }}
            >
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">🎨</Typography>
                <Typography variant="subtitle2" fontWeight="bold">Blancheur</Typography>
                <Typography variant="caption" color="text.secondary">L*a*b*</Typography>
                {selectedElements.includes('BLANCHEUR_L') && (
                  <Chip label="✓" size="small" color="success" sx={{ mt: 1 }} />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Granulométrie */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              elevation={selectedElements.includes('D80') ? 8 : 2}
              sx={{
                cursor: 'pointer',
                borderLeft: '4px solid #06b6d4',
                backgroundColor: selectedElements.includes('D80')
                  ? alpha('#06b6d4', 0.15)
                  : 'background.paper',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                transition: 'all 0.3s ease'
              }}
              onClick={() => {
                // Toggle granulométrie (D80) with routing
                const hasD80 = selectedElements.includes('D80');
                if (hasD80) {
                  const newElements = selectedElements.filter(e => e !== 'D80');
                  onElementsChange(newElements);
                  // Remove from routing
                  const newAssignments = { ...elementLabAssignments };
                  delete newAssignments['D80'];
                  setElementLabAssignments(newAssignments);
                  // Remove the D80 method
                  const d80Method = 'PRE.MO/ANA/048';
                  const newMethods = selectedMethods.filter(m => m !== d80Method);
                  setSelectedMethods(newMethods);
                  onMethodsChange(newMethods);
                } else {
                  const newElements = [...selectedElements, 'D80'];
                  onElementsChange(newElements);
                  // Add to routing
                  const newAssignments = { ...elementLabAssignments };
                  newAssignments['D80'] = 'LABORATOIRE_PHYSICO_CHIMIQUE';
                  setElementLabAssignments(newAssignments);
                  if (!customRoutingEnabled) setCustomRoutingEnabled(true);
                  // Add the D80 method
                  const d80Method = 'PRE.MO/ANA/048';
                  if (!selectedMethods.includes(d80Method)) {
                    const newMethods = [...selectedMethods, d80Method];
                    setSelectedMethods(newMethods);
                    onMethodsChange(newMethods);
                  }
                }
              }}
            >
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">📊</Typography>
                <Typography variant="subtitle2" fontWeight="bold">Granulométrie (D80)</Typography>
                <Typography variant="caption" color="text.secondary">Distribution granulaire - D80</Typography>
                {selectedElements.includes('D80') && (
                  <Chip label="✓" size="small" color="success" sx={{ mt: 1 }} />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* PERIODIC TABLE - Real layout */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          ⚛️ Tableau Périodique des Éléments
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Cliquez sur les éléments pour les sélectionner. Les éléments colorés ont des méthodes d'analyse disponibles.
        </Typography>

        {/* Enhanced Legend */}
        <Box sx={{ mb: 4, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
            🔍 Légende du Tableau Périodique
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 30, height: 30, 
                  backgroundColor: alpha('#FFD700', 0.15),
                  border: `2px solid ${alpha('#FFD700', 0.6)}`,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>
                  Au
                </Box>
                <Typography variant="caption" sx={{ fontWeight: '600' }}>
                  Métaux Précieux
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 30, height: 30, 
                  backgroundColor: alpha('#4CAF50', 0.15),
                  border: `2px solid ${alpha('#4CAF50', 0.6)}`,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>
                  Cu
                </Box>
                <Typography variant="caption" sx={{ fontWeight: '600' }}>
                  Métaux de Base
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 30, height: 30, 
                  backgroundColor: alpha('#2196F3', 0.15),
                  border: `2px solid ${alpha('#2196F3', 0.6)}`,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>
                  Fe
                </Box>
                <Typography variant="caption" sx={{ fontWeight: '600' }}>
                  Éléments Majeurs
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 30, height: 30, 
                  backgroundColor: alpha('#9C27B0', 0.15),
                  border: `2px solid ${alpha('#9C27B0', 0.6)}`,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>
                  As
                </Box>
                <Typography variant="caption" sx={{ fontWeight: '600' }}>
                  Éléments Traces
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          {/* Status indicators */}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 20, height: 20, 
                backgroundColor: '#4caf50',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.6rem',
                color: 'white',
                fontWeight: 'bold'
              }}>
                ✓
              </Box>
              <Typography variant="caption">Sélectionné</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 20, height: 20, 
                backgroundColor: '#ff9800',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.6rem',
                color: 'white',
                fontWeight: 'bold'
              }}>
                ⚠
              </Box>
              <Typography variant="caption">Pas de méthodes</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: '600' }}>
                Bordure épaisse = Méthodes disponibles
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Main Periodic Table */}
        <Box sx={{ 
          overflowX: 'auto', 
          mb: 4,
          p: 2,
          backgroundColor: '#fafafa',
          borderRadius: 2,
          border: '2px solid #e0e0e0'
        }}>
          <Box sx={{ minWidth: 800 }}>
            {PERIODIC_TABLE_LAYOUT.map((period, periodIndex) => (
              <Box key={periodIndex} sx={{ display: 'flex', mb: 0.5 }}>
                {period.map((element, index) => renderElementCell(element))}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Lanthanides */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: '#FF9800' }}>
            ✦ Lanthanides (Terres Rares)
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            p: 2,
            backgroundColor: alpha('#FF9800', 0.05),
            borderRadius: 2,
            border: `1px solid ${alpha('#FF9800', 0.2)}`
          }}>
            {LANTHANIDES.map(element => renderElementCell(element))}
          </Box>
        </Box>

        {/* Actinides */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 2, fontWeight: 'bold', color: '#E91E63' }}>
            ☢ Actinides (Éléments Radioactifs)
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            p: 2,
            backgroundColor: alpha('#E91E63', 0.05),
            borderRadius: 2,
            border: `1px solid ${alpha('#E91E63', 0.2)}`
          }}>
            {ACTINIDES.map(element => renderElementCell(element))}
          </Box>
        </Box>

        {/* 🆕 COMPOUND ELEMENTS - Oxides, Carbonates, Fluorides */}
        <Box sx={{ mb: 2, mt: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1565C0' }}>
            🧪 Composés / Oxydes (Sélectionnables manuellement ou via Méthodes Rapides)
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: 1,
            p: 2,
            backgroundColor: alpha('#1565C0', 0.05),
            borderRadius: 2,
            border: `1px solid ${alpha('#1565C0', 0.2)}`
          }}>
            {COMPOUND_ELEMENTS.map(compound => {
              const isSelected = selectedElements.includes(compound.symbol);
              return (
                <Tooltip 
                  key={compound.symbol} 
                  title={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {compound.name}
                      </Typography>
                      <Typography variant="body2">
                        {compound.symbol} - {compound.category}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#4caf50' }}>
                        ✓ Méthodes disponibles
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Chip
                    label={compound.symbol}
                    onClick={() => handleElementToggle(compound.symbol)}
                    sx={{
                      fontWeight: isSelected ? 'bold' : 'normal',
                      backgroundColor: isSelected ? alpha(compound.color, 0.3) : alpha(compound.color, 0.1),
                      borderColor: isSelected ? compound.color : alpha(compound.color, 0.5),
                      border: `2px solid`,
                      color: isSelected ? '#000' : '#333',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: alpha(compound.color, 0.25),
                        borderColor: compound.color,
                        transform: 'scale(1.05)',
                      },
                      ...(isSelected && {
                        boxShadow: `0 2px 8px ${alpha(compound.color, 0.4)}`,
                      })
                    }}
                    icon={isSelected ? <CheckCircleIcon sx={{ color: '#4caf50' }} /> : undefined}
                  />
                </Tooltip>
              );
            })}
          </Box>
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
            💡 Ces composés sont automatiquement inclus dans les méthodes ICP (PRE.MO/ANA/007, 037, 038, 039, 041)
          </Typography>
        </Box>
      </Paper>

      {/* Methods Section (KEEP AS REQUESTED - shows methods after selecting elements) */}
      {renderMethodsSection()}

      {/* 🔬 CUSTOM LAB ROUTING SECTION */}
      {selectedElements.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mt: 3, mb: 3, borderRadius: 2, border: '2px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <AltRouteIcon sx={{ mr: 1, color: 'primary.main' }} />
              🔬 Routage Multi-Laboratoire Personnalisé
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={customRoutingEnabled}
                  onChange={(e) => {
                    setCustomRoutingEnabled(e.target.checked);
                    if (e.target.checked) {
                      // 🔧 FIX: Preserve Physico elements routing when enabling custom routing
                      const physicoElements = ['DENSITE', 'H2O', 'PAF', 'MO', 'PH', 'BLANCHEUR_L', 'BLANCHEUR_A', 'BLANCHEUR_B', 'D80'];
                      const defaultAssignments: Record<string, string> = {};
                      selectedElements.forEach(el => {
                        // Keep existing assignment if present, otherwise default based on element type
                        if (elementLabAssignments[el]) {
                          defaultAssignments[el] = elementLabAssignments[el];
                        } else if (physicoElements.includes(el)) {
                          defaultAssignments[el] = 'LABORATOIRE_PHYSICO_CHIMIQUE';
                        } else {
                          defaultAssignments[el] = 'LABORATOIRE_ICP';
                        }
                      });
                      setElementLabAssignments(defaultAssignments);
                    } else {
                      setElementLabAssignments({});
                    }
                  }}
                  disabled={disabled}
                  color="primary"
                />
              }
              label={customRoutingEnabled ? "Activé" : "Désactivé"}
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Activez cette option pour envoyer différents éléments vers différents laboratoires (SAA, ICP, AU).
            Les échantillons seront traités en parallèle dans chaque laboratoire sélectionné.
          </Typography>

          {customRoutingEnabled && (
            <Box>
              {/* Quick-select buttons */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  ⚡ Sélection Rapide:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => setAllElementsToLab('LABORATOIRE_SAA')}
                    sx={{ borderColor: '#FF9800', color: '#FF9800', '&:hover': { borderColor: '#FF9800', backgroundColor: 'rgba(255,152,0,0.1)' } }}
                  >
                    Tout → SAA
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => setAllElementsToLab('LABORATOIRE_ICP')}
                    sx={{ borderColor: '#2196F3', color: '#2196F3', '&:hover': { borderColor: '#2196F3', backgroundColor: 'rgba(33,150,243,0.1)' } }}
                  >
                    Tout → ICP
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => setAllElementsToLab('LABORATOIRE_AU')}
                    sx={{ borderColor: '#FFD700', color: '#B8860B', '&:hover': { borderColor: '#FFD700', backgroundColor: 'rgba(255,215,0,0.1)' } }}
                  >
                    Tout → AU
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => setAllElementsToLab('LABORATOIRE_PHYSICO_CHIMIQUE')}
                    sx={{ borderColor: '#059669', color: '#059669', '&:hover': { borderColor: '#059669', backgroundColor: 'rgba(5,150,105,0.1)' } }}
                  >
                    Tout → Physico
                  </Button>
                </Stack>
              </Box>

              {/* Element-by-element assignment grid */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                📋 Affectation par Élément:
              </Typography>
              <Grid container spacing={1}>
                {selectedElements.map(element => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={element}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 0.5,
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: alpha(getLabColor(elementLabAssignments[element] || 'LABORATOIRE_ICP'), 0.1),
                      border: `1px solid ${alpha(getLabColor(elementLabAssignments[element] || 'LABORATOIRE_ICP'), 0.3)}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ 
                          width: 35, 
                          fontWeight: 'bold',
                          fontSize: '0.9rem',
                          color: getLabColor(elementLabAssignments[element] || 'LABORATOIRE_ICP')
                        }}>
                          {element}
                        </Typography>
                        <Select
                          value={elementLabAssignments[element] || 'LABORATOIRE_ICP'}
                          onChange={(e) => handleLabChange(element, e.target.value as string)}
                          size="small"
                          sx={{ 
                            flex: 1, 
                            minWidth: 70,
                            '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' }
                          }}
                          disabled={disabled}
                        >
                          <MenuItem value="LABORATOIRE_SAA">SAA</MenuItem>
                          <MenuItem value="LABORATOIRE_ICP">ICP</MenuItem>
                          <MenuItem value="LABORATOIRE_AU">AU</MenuItem>
                          <MenuItem value="LABORATOIRE_PHYSICO_CHIMIQUE">Physico</MenuItem>
                        </Select>
                      </Box>
                      {/* 🆕 Show selected method for this element */}
                      {elementMethodMapping[element] && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '0.7rem', 
                            color: 'text.secondary',
                            backgroundColor: alpha(getLabColor(elementLabAssignments[element] || 'LABORATOIRE_ICP'), 0.15),
                            px: 0.5,
                            py: 0.25,
                            borderRadius: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          📋 {elementMethodMapping[element]}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Summary of routing */}
              <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  📊 Résumé du Routage:
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  {/* 🔧 FIX: Added LABORATOIRE_PHYSICO_CHIMIQUE to show Physico elements in summary */}
                  {['LABORATOIRE_SAA', 'LABORATOIRE_ICP', 'LABORATOIRE_AU', 'LABORATOIRE_PHYSICO_CHIMIQUE'].map(lab => {
                    const elementsForLab = Object.entries(elementLabAssignments)
                      .filter(([_, assignedLab]) => assignedLab === lab)
                      .map(([el, _]) => el);
                    if (elementsForLab.length === 0) return null;
                    return (
                      <Chip
                        key={lab}
                        label={`${getLabDisplayName(lab)}: ${elementsForLab.join(', ')}`}
                        sx={{ 
                          backgroundColor: alpha(getLabColor(lab), 0.2),
                          fontWeight: 'bold',
                          '& .MuiChip-label': { fontSize: '0.85rem' }
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* Empty State */}
      {selectedElements.length === 0 && (
        <Paper 
          elevation={1} 
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            backgroundColor: 'rgba(0,0,0,0.02)',
            border: '2px dashed rgba(0,0,0,0.1)',
            borderRadius: 2
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Sélectionnez des éléments pour commencer
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Utilisez les méthodes rapides ou sélectionnez des éléments dans le tableau périodique
          </Typography>
        </Paper>
      )}

      {/* 🆕 NEW: Method Selection Modal */}
      <Dialog 
        open={methodModalOpen} 
        onClose={handleMethodModalCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: alpha(getLabColor(methodModalLab), 0.1),
          borderBottom: `3px solid ${getLabColor(methodModalLab)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScienceIcon sx={{ color: getLabColor(methodModalLab) }} />
            <Box>
              <Typography variant="h6">
                Sélectionner la méthode pour {methodModalElement}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Laboratoire: {getLabDisplayName(methodModalLab)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {methodModalLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : methodModalMethods.length === 0 ? (
            <Alert severity="warning">
              Aucune méthode disponible pour {methodModalElement} dans ce laboratoire.
            </Alert>
          ) : (
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
                Méthodes disponibles ({methodModalMethods.length})
              </FormLabel>
              <RadioGroup>
                {methodModalMethods.map((method) => (
                  <Paper 
                    key={method.method_code}
                    elevation={1}
                    sx={{ 
                      p: 2, 
                      mb: 1, 
                      cursor: 'pointer',
                      border: '2px solid transparent',
                      '&:hover': { 
                        backgroundColor: alpha(getLabColor(methodModalLab), 0.05),
                        borderColor: alpha(getLabColor(methodModalLab), 0.3)
                      },
                      transition: 'all 0.2s'
                    }}
                    onClick={() => handleMethodSelect(methodModalElement, methodModalLab, method.method_code)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Radio 
                        value={method.method_code}
                        checked={elementMethodMapping[methodModalElement] === method.method_code}
                        sx={{ mt: -0.5 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {method.method_name}
                          </Typography>
                          {method.is_accredited && (
                            <Chip 
                              label="✓ ACCRÉDITÉ" 
                              size="small" 
                              color="success" 
                              sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', mb: 0.5 }}>
                          {method.method_code}
                        </Typography>
                        {method.detection_limits && method.detection_limits[methodModalElement] && (
                          <Typography variant="caption" sx={{ 
                            backgroundColor: '#f5f5f5', 
                            px: 1, 
                            py: 0.25, 
                            borderRadius: 1,
                            display: 'inline-block'
                          }}>
                            Limite: {method.detection_limits[methodModalElement].limit} {method.detection_limits[methodModalElement].unit}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleMethodModalCancel} color="inherit">
            Annuler
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ElementSelector; 